/**
 * useStudentActivityExit — où l'élève va quand une activité se termine, hors live.
 *
 * L'enchaînement de l'ancien écran de fin (`CongratsScreen`, `autoSelectNext`) : l'activité suivante de
 * la todo (`getMostRecentActivity`, comme `useNextTodoActivity`), par la même porte que le lanceur
 * (`studentActivityLaunch` : `sam-session` si Sam est ouvert, sinon `activity`), à la place de celle
 * qui se termine — la pile ne grossit pas d'une activité à l'autre. Sans suite, ou sur « Retour à mes
 * activités », retour à la liste. L'activité retirée par le tuteur passe à la suivante
 * (`ActivityWrapperScreen.handleContinue`).
 */
import { useNavigation } from "@react-navigation/native"
import { useCallback, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"

import { isSamOpenFor } from "../../../../../../data/constants/samConstants"
import { setCurrentActivity } from "../../../../../../data/redux/actions/activity"
import { type ApiRunActivity } from "../../../../adapters/student/Activity/studentActivityAdapter"
import { studentActivityLaunch } from "../../../hooks/useLaunchStudentActivity"
import { type StudentActivityOutcome } from "./StudentActivityBody"
import { selectDisplayedStudent } from "../../../../view-models/shared/displayedStudent"

type ExitNavigation = {
    canGoBack: () => boolean
    goBack: () => void
    replace: (route: string, params?: object) => void
}

export function useStudentActivityExit(nextActivity: ApiRunActivity | null) {
    const navigation = useNavigation() as unknown as ExitNavigation
    const dispatch = useDispatch()
    const samOpen = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) => isSamOpenFor(selectDisplayedStudent(state)) as boolean
    )

    const backToActivities = useCallback(() => {
        dispatch(setCurrentActivity(null) as never)
        if (navigation.canGoBack()) navigation.goBack()
        else navigation.replace("menu", { screen: "activities" })
    }, [dispatch, navigation])

    const launchNext = useCallback(
        (forceSam: boolean) => {
            if (nextActivity?.id == null) {
                backToActivities()
                return
            }
            const launch = studentActivityLaunch(nextActivity.id, samOpen || forceSam)
            if (launch.route === "activity") {
                dispatch(setCurrentActivity({ activityData: nextActivity, progressStatus: "ongoing" }) as never)
            }
            navigation.replace(launch.route, launch.params)
        },
        [backToActivities, dispatch, navigation, nextActivity, samOpen]
    )

    // On ne quitte l'écran qu'une fois. Un second geste (« Retour à mes activités » touché deux fois, le compte à
    // rebours des félicitations qui finit pendant la sortie) partirait de l'écran qui s'en va, et la pile, qui
    // n'en vérifie pas l'origine, dépilerait ou remplacerait l'écran d'en dessous.
    const left = useRef(false)
    return useCallback(
        (outcome: StudentActivityOutcome) => {
            // L'élève reste sur l'activité rendue (en Dialogue, dans le live seulement).
            if (outcome.kind === "submitted" || left.current) return
            left.current = true
            switch (outcome.kind) {
                case "done":
                    if (outcome.next === "activities") backToActivities()
                    else launchNext(outcome.next === "sam")
                    return
                case "deleted":
                    launchNext(false)
                    return
                default:
                    backToActivities()
            }
        },
        [backToActivities, launchNext]
    )
}
